import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoginData } from '../interfaces/Iip-login';
import { User } from '../interfaces/Iip-user';
import { IpRoleService } from './ip-role.service';
import { RoleResponse } from '../interfaces/ip-role-response';

@Injectable({
  providedIn: 'root',
})
export class IpAuthService {
  api = environment.api;
  private sessionSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public currentSession$: Observable<boolean> =
    this.sessionSubject.asObservable();
  private userRole?: RoleResponse;
  private otpSessionId: string | null = null;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private ipRoleService: IpRoleService,
    private dialogRef: MatDialog
  ) {
    if (this.getToken()) {
      this.sessionSubject.next(true);
    }
  }

  public login(
    loginUser: LoginData,
    isAdminLogin: boolean = false
  ): Observable<User> {
    if (isAdminLogin) {
      return this.httpClient
        .post<User>(`${this.api}/invopay/users/signIn`, loginUser)
        .pipe(map((resp) => this.mapUser(resp)));
    } else {
      return this.httpClient
        .post<User>(`${this.api}/invopay/users/signIn/employee`, loginUser)
        .pipe(map((resp) => this.mapUser(resp)));
    }
  }

  public getOtpSessionId(): string | null {
    return this.otpSessionId;
  }

  private mapUser(resp: User) {
    if (resp.requires2fa && resp.otpSessionId) {
      console.log('Mapping user with response:', resp);
      this.otpSessionId = resp.otpSessionId;
      return resp;
    }
    if (resp.access_token) {
      // TODO: remove this line:
      // Just in case:
      sessionStorage.clear()
      // It's important to set 'path'
      // Otherwise, they won't be deleted afterwards
      sessionStorage.setItem('access_token', resp.access_token);
      sessionStorage.setItem('username', resp.fullName || resp.username); 
      sessionStorage.setItem('userType', resp.userType);
      sessionStorage.setItem('role', resp?.roleResponse?.name || resp.userType); 
      if (resp.userStatus) sessionStorage.setItem('userStatus', resp.userStatus);
      if(resp.supplierSignInReponse){
        sessionStorage.setItem('isBroker', resp.supplierSignInReponse.isBroker);
      }
      if(resp.roleResponse){
        this.setUserRole(resp.roleResponse)
      }
      this.sessionSubject.next(true);
      return resp;
    }
    throw new Error('Missing token.');
  }

  public getToken() {
    return sessionStorage.getItem('access_token') ?? false;
  }

  public logOut() {
    if (this.getToken()) {
      // This checks wether the user was logged in a an employee
      // Why? So that they get redirected back to employees' login
      // and not to admins' login
      const wasEmployee = this.ipRoleService.getType() === 'employee';
      let wasSupplier = false;
      let wasBroker = false;
      if ( this.ipRoleService.getType() === 'provider' ){
        wasSupplier = !this.ipRoleService.getIsBroker()
        wasBroker = this.ipRoleService.getIsBroker()
      }
      sessionStorage.clear()
      this.dialogRef.closeAll();
      this.sessionSubject.next(false);
      if (wasEmployee) {
        this.router.navigateByUrl('invopay/broker/login-employee');
      } else if (wasSupplier){
        this.router.navigateByUrl('invopay/broker/login-supplier');
      } else if (wasBroker){
        this.router.navigateByUrl('invopay/broker/login-broker');
      } else {
        this.router.navigateByUrl('invopay/broker/login-admin');
      }
    }
  }

  public sendResetPasswordEmail(email: string) {
    return this.httpClient.get(
      `${this.api}/invopay/users/reset-password-email`,
      { params: { email } }
    );
  }

  public resetPassword(newPassword: string, token: string, captchaToken: string|null=null) {
    return this.httpClient.post<User>(`${this.api}/invopay/users/reset-password`, {
      newPassword, token, captchaToken
    }).pipe(map((resp) => this.mapUser(resp)));
  }

  public getUserRole(){
    return this.userRole
  }

  public setUserRole(userRole:RoleResponse){
    this.userRole = userRole
  }

  public authWithJwt(
    jwt: string
  ): Observable<User> {
    const headers = new HttpHeaders({
      'jwt': jwt
    });
    return this.httpClient
      .post<User>(`${this.api}/invopay/users/signInWithJwt`, jwt, {headers})
      .pipe(map((resp) => this.mapUser(resp)));
  }

  public getSecurityKey(
    enterpriseId: number
  ): Observable<string> {
    const headers = new HttpHeaders({
      'enterpriseId': enterpriseId
    });
    return this.httpClient
      .post<string>(`${this.api}/invopay/users/securityKey`, null, {headers})
  }

  public getTokenFromSecurityKey(
    securityKey: string,
    email: string
  ): Observable<string> {
    const headers = new HttpHeaders({
      'securityKey': securityKey,
      'email': email
    });
    return this.httpClient
      .post<string>(`${this.api}/invopay/users/tokenFromSecurityKey`, null, {headers})
  }

  public tokenLoginBroker(
    token: string,
  ): Observable<User> {
      return this.httpClient
        .post<User>(`${this.api}/invopay/users/signInWithToken/broker`, { token })
        .pipe(map((resp) => this.mapUser(resp)));
  }

  public isOnlyReadMode(): boolean {
    const status = sessionStorage.getItem('userStatus'); 
    return status ? status === 'INACTIVE' : false;
  }

  public verifyOtp(code: string): Observable<User> {
    const sessionId = this.otpSessionId;
    if (!sessionId) {
      throw new Error('No OTP session active.');
    }
    return this.httpClient.post<User>(`${this.api}/invopay/users/verify-otp`, { sessionId, code }).pipe(
      tap(() => (this.otpSessionId = null)),
      map((resp) => this.mapUser(resp))
    );
  }
}
